import { camelCase, capitalCase, snakeCase, constantCase } from "change-case";

// Define known acronyms (you can expand this list as needed)
const KNOWN_ACRONYMS = new Set(["DME", "EHR", "ID", "SMS", "VH"]);

export const customSnakeCase = (str: string) => snakeCase(str, { separateNumbers: true });
export const customCamelCase = (str: string) =>
  camelCase(str.replace(/[_\s](\d+)/g, "$1"), {
    separateNumbers: false,
  });

export const customConstantCase = (str: string) => constantCase(str, { separateNumbers: true });

export const customCapitalCase = (str: string | null | undefined) => {
  if (!str) return null;
  const trimmed = str.trim();

  // Regex to match all common dash types, period, apostrophes, double quotes, commas, and parentheses
  const separatorRegex = /([.\-\u2012\u2013\u2014\u2015\u2212'’",()])/g;
  // Split and keep the separator as a separate element
  const parts = trimmed.split(separatorRegex);

  // Map: capitalize non-separator parts, leave separators as-is
  return parts
    .map((part) => (separatorRegex.test(part) ? part : capitalCase(part, { separateNumbers: false })))
    .join("");
};

export const RC_INTERNAL_VH_FAX_NUMBER = "+14156923411";

// Generate a deterministic color from a string
export const stringToColor = (str: string | null) => {
  if (!str) return "hsl(0, 0%, 40%)";

  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Define more distinctive predefined colors with high contrast
  const colors = [
    "hsl(0, 85%, 60%)", // Red
    "hsl(30, 90%, 50%)", // Orange
    "hsl(60, 90%, 45%)", // Yellow
    "hsl(120, 75%, 40%)", // Green
    "hsl(180, 80%, 40%)", // Teal
    "hsl(210, 90%, 60%)", // Blue
    "hsl(240, 80%, 65%)", // Indigo
    "hsl(280, 85%, 60%)", // Purple
    "hsl(320, 90%, 55%)", // Pink
    "hsl(350, 85%, 55%)", // Red-pink
    "hsl(90, 80%, 45%)", // Yellow-green
    "hsl(150, 75%, 45%)", // Blue-green
    "hsl(20, 80%, 50%)", // Red-orange
    "hsl(270, 85%, 60%)", // Violet
    "hsl(300, 80%, 55%)", // Magenta
    "hsl(45, 90%, 50%)", // Yellow-orange
    "hsl(195, 85%, 50%)", // Sky blue
  ];

  // Use the hash to select a color
  const colorIndex = Math.abs(hash) % colors.length;

  return colors[colorIndex];
};

/**
 * Renders an enum-keyed description map as a bullet list for an LLM prompt or a Zod `.describe()`.
 *
 * Key the map by the full enum (`Record<MyEnum, string>`) so adding a value forces adding its
 * description — a compile error rather than a value the model sees with no explanation.
 */
export function toEnumBullets<K extends string>(descriptions: Record<K, string>): string {
  return Object.entries<string>(descriptions)
    .map(([value, description]) => `- ${value} — ${description}`)
    .join("\n");
}

export function formatFromDashToHumanReadable(inputString: string): string {
  return formatToHumanReadable(inputString, "-");
}

export function formatFromUnderscoreToHumanReadable(inputString: string): string {
  return formatToHumanReadable(inputString, "_");
}

export function formatToHumanReadable(inputString: string, separator: string): string {
  if (!inputString) {
    return "";
  }

  return inputString
    .split(separator)
    .map((word) => {
      // Check if the uppercase version of the word is a known acronym
      if (KNOWN_ACRONYMS.has(word.toUpperCase())) {
        return word.toUpperCase();
      }

      // Original capitalization logic
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function capitalizeFirstLetter(value: string): string {
  return value.replace(/[a-z]/i, (letter) => letter.toUpperCase());
}

function smartTitleCase(str: string): string {
  if (!str) return "";

  // If it's all caps, convert to proper case (for names)
  if (str === str.toUpperCase() && str.length > 1) {
    return capitalizeFirstLetter(str.toLowerCase());
  }

  // If it has mixed case and looks intentional (like camelCase), preserve it
  if (str !== str.toLowerCase() && str !== str.toUpperCase()) {
    // Check if it looks like camelCase, PascalCase, or has intentional caps
    const hasIntentionalCaps = /[A-Z]/.test(str.slice(1));

    if (hasIntentionalCaps) {
      return str;
    }
  }

  // Handle names with apostrophes (like O'Connor, D'Angelo)
  if (str.includes("'")) {
    return str
      .split("'")
      .map((part, index) => {
        if (index === 0) {
          // First part: capitalize first letter
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        } else {
          // After apostrophe: capitalize first letter
          return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        }
      })
      .join("'");
  }

  // Default: first letter uppercase, rest lowercase
  return capitalizeFirstLetter(str.toLowerCase());
}

export function properCase(input: string | null | undefined): string {
  if (!input) return "";

  return input
    .trim()
    .split(/\s+/)
    .map((token) => {
      // Check if token contains any dash
      const hasDash = /[\u2012\u2013\u2014\u2015\u2212-]/.test(token);

      if (hasDash) {
        // Split by dashes, apply title case to each part, then rejoin with dashes
        return token
          .split(/[\u2012\u2013\u2014\u2015\u2212-]/)
          .map((part) => smartTitleCase(part))
          .join("-");
      } else {
        // No dash, just apply title case
        return smartTitleCase(token);
      }
    })
    .join(" ");
}

export function camelCaseToHuman(input: string): string {
  if (!input) return "";

  return input
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}
